'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import Image from 'next/image';
import { useRef } from 'react';
import { CmsImageItemWrapper } from './CmsImageItemWrapper.client';
import { GroupOnImage } from './GroupOnImage';
import type { CmsImageType } from './types';
import { useImageData } from './utils/useImageData';
import { Decoline } from '../Decoline/Decoline';
import { parseBorderRadius } from '../../utils/borderRadius';

export type CmsImageItemProps = {
  data: CmsImageType;
};

export const CmsImageItem = ({ data }: CmsImageItemProps) => {
  const {
    assets: {
      cmsImage: {
        imageOptimizationEnabled,
        uriTransformSource,
        uriTransformEndpoint,
        imageQuality,
      },
    },
  } = useConfig();

  const imgref = useRef<HTMLImageElement>(null);

  const {
    field_display_variant: fieldDisplayVariant,
    field_alternate_text: altText = '',
    field_lazyload: lazyLoading = true,
    behavior_paragraph_borderRadius: borderRadius,
    behavior_paragraph_deco_line: hasDecoline,
    paragraph,
  } = data;

  const { imageData, isVectorImage, imageDataHeight, imageDataWidth } = useImageData(data);

  if (!imageData?.url) {
    return null;
  }

  const useNextJsImage = imageDataWidth && imageDataHeight && !isVectorImage;

  return (
    <CmsImageItemWrapper
      data={data}
      groupOnImage={paragraph && <GroupOnImage paragraph={paragraph} />}
      imgref={imgref}
      isVectorImage={isVectorImage}
    >
      <>
        {useNextJsImage && (
          <Image
            key={`cmsImageItem-${data.id}`}
            ref={imgref}
            src={
              imageOptimizationEnabled && typeof imageData?.uri === 'string'
                ? imageData.uri.replace(uriTransformSource, uriTransformEndpoint)
                : imageData.url
            }
            alt={altText ?? ''}
            width={parseInt(imageDataWidth, 10)}
            height={parseInt(imageDataHeight, 10)}
            style={{
              objectFit: 'contain',
              borderRadius: fieldDisplayVariant === 'circle' ? '50%' : borderRadius,
            }}
            fetchPriority={lazyLoading ? 'low' : 'high'}
            {...(lazyLoading && { loading: 'lazy' })}
            {...(!lazyLoading && { priority: true })}
            {...(imageOptimizationEnabled && { quality: imageQuality })}
            {...(!imageOptimizationEnabled && { unoptimized: true })}
          />
        )}
        {!useNextJsImage && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            ref={imgref}
            src={imageData?.url}
            alt={altText ?? ''}
            loading={lazyLoading ? 'lazy' : 'eager'}
            style={{ borderRadius: fieldDisplayVariant === 'circle' ? '50%' : borderRadius }}
          />
        )}
        {hasDecoline && (
          <Decoline borderRadius={borderRadius ? parseBorderRadius(borderRadius) : undefined} />
        )}
      </>
    </CmsImageItemWrapper>
  );
};
